/*
 * Вкладка «Проверить» на /document-signer — единственная проверка электронной
 * подписи на сайте (до 23.10.2026 — отдельная страница /signature-check, теперь
 * 301 сюда). Движок: intake.js → check.js (+ trust.js); здесь только раскладка
 * ответа, документ на экране, протокол, отметка об ЭП для печати и учёт.
 * Вкладки и «Подписать» — public/js/document-signer/* (классические скрипты).
 *
 * Порядок ответа — порядок вопросов человека: подпись верна ли (и почему
 * нет — простыми словами) → кто подписал и когда → сам документ → по пунктам
 * → сертификат подробно. Доверие к УЦ и отзыв приходят вторыми (сеть):
 * пункт сначала «проверяем…», потом ответ — на месте, без перерисовки всего.
 */
import { intakeAll, stripSigExt } from './intake.js';
import { buildCases, pair, runCase, enrichCase, caseCode, sniffDoc, certPrints } from './check.js';
import { parseXml, decodeXml, pretty } from './xml.js';
import { summarize, renderTree } from './xmlview.js';
import { protocolPdf } from './report.js';
import { stampPdf, stampable } from './stamp.js';

const W = window;
const S = W.__SC || {};
const T = S.i18n || {};
const SLUG = S.slug || 'document-signer';
const SIG_ROLES = { cms: 1, pdf: 1, xmlsig: 1, asic: 1, eml: 1, seal: 1 };

/* ═══ строки и форматы ══════════════════════════════════════════════ */

function t(k, p) {
    let s = T[k];
    if (s == null) return k;
    if (!p) return s;
    return String(s).replace(/:([a-z]+)/gi, function (m, n) { return Object.prototype.hasOwnProperty.call(p, n) ? String(p[n]) : m; });
}
function loc() { try { return W.__intlLocale ? W.__intlLocale() : (S.locale || 'en'); } catch (e) { return 'en'; } }
function fmtDate(d, withTime) {
    if (!d) return '';
    try {
        const o = withTime === false ? { day: 'numeric', month: 'long', year: 'numeric' } : { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' };
        return new Intl.DateTimeFormat(loc(), o).format(new Date(d));
    } catch (e) { return String(d); }
}
// Дробь — по языку страницы («1,5 МБ» в ru, «1.5 MB» в en), а не toFixed с точкой.
function fmt1(x) { try { return new Intl.NumberFormat(loc(), { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(x); } catch (e) { return x.toFixed(1); } }
function fmtSize(b) {
    if (b >= 1073741824) return fmt1(b / 1073741824) + ' ' + t('u_gb');
    if (b >= 1048576) return fmt1(b / 1048576) + ' ' + t('u_mb');
    if (b >= 1024) return Math.round(b / 1024) + ' ' + t('u_kb');
    return (b || 0) + ' ' + t('u_b');
}

/* ═══ элементы ══════════════════════════════════════════════════════ */

function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}
// Данные человека (имя, организация, имя файла) — в <bdi>: в арабском интерфейсе
// «ООО «Ромашка»» иначе теряет кавычку (тот же урок, что у /document-signer).
function dataEl(tag, cls, text) { const e = el(tag, cls); e.appendChild(el('bdi', null, text == null ? '' : String(text))); return e; }
const ICON = {
    ok: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M8.5 12.2l2.4 2.4 4.8-5"/></svg>',
    bad: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9.5 9.5l5 5M14.5 9.5l-5 5"/></svg>',
    warn: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 7.5v5.5M12 16.5h.01"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 11v5M12 7.5h.01"/></svg>',
    'c-ok': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.8 2.8L16 10"/></svg>',
    'c-bad': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/></svg>',
    'c-warn': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9.5 16.5h-19z"/><path d="M12 10v4M12 17h.01"/></svg>',
    'c-na': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12h8"/></svg>',
    'c-wait': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="9" stroke-dasharray="4 4"/></svg>'
};
function icon(name) { const s = el('span', 'sc-ico'); s.setAttribute('aria-hidden', 'true'); s.innerHTML = ICON[name] || ''; return s; }
function btn(label, cls, onClick) {
    const b = el('button', 'tc-btn sm ' + (cls || ''), label);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
}
function ae(f, extra) { try { if (W.__ae) W.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, extra || {})); } catch (e) { /* telemetry never breaks the tool */ } }
function stage(s, meta) { try { if (W.__stage) W.__stage(s, meta || {}); } catch (e) { /* noop */ } }
function fail(err, where, say) { try { if (W.__handleError) W.__handleError(err, where, say ? { say: say } : undefined); else if (W.__toast && say) W.__toast(say, 'error'); } catch (e) { /* noop */ } }
function save(blob, name) { return W.__downloadFile ? W.__downloadFile(blob, name) : Promise.resolve(false); }
function safeName(n, fb) {
    let s = String(n || '').trim();
    if (W.__outputName && W.__outputName.sanitize) s = W.__outputName.sanitize(s);
    else s = s.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').trim();
    return s || fb || 'document';
}

/* ═══ состояние ═════════════════════════════════════════════════════ */

const st = { items: [], cases: [], run: 0, token: null, tokenKey: null, paidKeys: new Set() };
const $ = function (id) { return document.getElementById(id); };
let els = {};

function busy(text) { els.busy.hidden = !text; if (text) els.busyText.textContent = text; }

/* ═══ вердикты → слова ══════════════════════════════════════════════ */

const LVL = { too_big: 'info', broken_sig: 'warn', ok: 'ok', revision: 'warn', cert_time: 'warn', need_doc: 'info', unknown: 'info', modified: 'bad', bad: 'bad', revoked: 'bad', none: 'info', nosig: 'info', pdf_stamp: 'warn', pdf_field: 'info' };
const UNTRUSTED = { self_signed: 1, not_listed: 1, ru_not_accredited: 1, eu_not_qualified: 1, list_mismatch: 1 };
function untrusted(c) {
    return !!(c && c.signers && c.signers.some(function (m) { return m.kind !== 'doc_ts' && m.trust && UNTRUSTED[m.trust.state]; }));
}
// «Подпись цела» без подтверждённой личности подписанта — не зелёный ответ.
function lvlOf(code, c) {
    if (code === 'ok' && untrusted(c)) return 'warn';
    return LVL[code] || 'info';
}

function verdictTitle(c) {
    const n = c.signers.length;
    switch (c.code) {
        case 'ok':
            if (c.type === 'seal') return t('v_seal_ok');
            if (n && c.signers.every(function (m) { return m.kind === 'doc_ts'; })) return t('v_ts_only');
            return t(n > 1 ? 'v_ok_many' : 'v_ok');
        case 'need_doc': return t(c.type === 'asic' ? 'v_need_doc_inside' : 'v_need_doc');
        case 'modified':
            if (c.type === 'asic' && c.asic && c.asic.uncovered.length) return t('v_modified_asic');
            return t(insideDoc(c) ? 'v_modified_inside' : 'v_modified');
        case 'bad': return c.type === 'seal' ? t('v_seal_bad') : t('v_bad');
        case 'cert_time': return t('v_cert_time');
        case 'revoked': return t('v_revoked');
        case 'revision': return t('v_revision');
        case 'nosig': return t('v_nosig');
        case 'broken_sig': return t('v_broken_sig');
        case 'too_big': return t('v_too_big', { size: fmtSize(c.item ? c.item.size : 0) });
        case 'pdf_stamp': return t('v_pdf_stamp');
        case 'pdf_field': return t('v_pdf_field');
        case 'none':
            if (c.type === 'cert') return t('v_cert_only');
            if (c.type === 'pgp') return t('v_pgp');
            if (c.type === 'crl') return t('v_crl');
            return t('v_nosig');
        default: return t('v_unknown');
    }
}

// Документ внутри самой подписи (PDF, прикреплённая, XML, контейнер) — «подпись
// от другого файла» здесь невозможна, остаётся только «изменён».
function insideDoc(c) {
    return !!(c && (c.type === 'pdf' || c.type === 'xml' || c.type === 'asic' || c.type === 'eml' || (c.type === 'cms' && c.attached)));
}

function whoLine(c) {
    const people = c.signers.filter(function (m) { return m.kind !== 'doc_ts' && m.name; });
    if (!people.length) return '';
    const first = people[0];
    let s = people.length > 1 ? t('signed_by_many', { who: first.name, n: people.length - 1 }) : t('signed_by', { who: first.name });
    if (first.when) s += ' · ' + t(first.timeKind === 'timestamp' ? 'when_ts' : 'when_claimed', { date: fmtDate(first.when) });
    return s;
}

function revWhy(e) {
    e = String(e || '');
    if (/^upstream_/.test(e)) return 'UPSTREAM';
    if (/host_not_allowed|redirect_not_allowed/.test(e)) return 'HOST';
    if (/fetch_failed|host_unresolvable|NETWORK|TIMEOUT|UNREACHABLE|HTTP_5/.test(e)) return 'UNREACHABLE';
    return T['rev_why_' + e] ? e : 'other';
}
function reasonText(r) { return r ? t('reason_' + r) : t('reason_unspecified'); }

/** Пункты проверки подписанта — по порядку вопросов человека. */
function checksOf(m) {
    const C = [];
    if (m.kind === 'doc_ts') {
        C.push(m.integrity === 'ok' ? { lvl: 'ok', text: t('c_doc_ts', { tsa: m.name || (m.timestamp && m.timestamp.tsa) || t('tsa'), time: fmtDate(m.when) }) }
            : m.integrity === 'fail' ? { lvl: 'bad', text: t('c_integrity_fail') } : { lvl: 'na', text: t('c_integrity_missing') });
        if (m.signature === false) C.push({ lvl: 'bad', text: t('c_sig_bad') });
        return C;
    }
    if (m.integrity === 'ok') C.push({ lvl: 'ok', text: t('c_integrity_ok') });
    else if (m.integrity === 'fail') C.push({ lvl: 'bad', text: t(m.inside ? 'c_integrity_fail_inside' : 'c_integrity_fail') });
    else if (m.integrity === 'unsupported') C.push({ lvl: 'na', text: t('c_integrity_unsupported') });
    else {
        // Контейнер: подписанных файлов в нём нет — называем их, а не просим «добавить документ».
        const miss = m.inside ? (m.refs || []).filter(function (r) { return r.external && r.ok === null && r.uri; }).map(function (r) { try { return decodeURIComponent(r.uri); } catch (e) { return r.uri; } }) : [];
        if (miss.length) C.push({ lvl: 'na', text: t('c_integrity_missing_inside', { names: miss.slice(0, 5).join(', ') + (miss.length > 5 ? ' …' : '') }) });
        else C.push({ lvl: 'na', text: t('c_integrity_missing'), action: 'add_doc' });
    }
    if (m.pdf && !m.pdf.coversWhole) C.push(m.pdf.laterSigned ? { lvl: 'na', text: t('c_pdf_later_sig') } : { lvl: 'warn', text: t('c_pdf_partial') });

    if (m.signature === true) C.push({ lvl: 'ok', text: t('c_sig_ok', { name: m.name || m.cn || '—' }) });
    else if (m.signature === false) C.push({ lvl: 'bad', text: t('c_sig_bad') });
    else if (m.signatureReason === 'NO_CERT') C.push({ lvl: 'na', text: t('c_sig_no_cert'), action: 'add_cert' });
    else {
        const alg = (m.cert && m.cert.publicKey && m.cert.publicKey.algorithmOid) || '';
        C.push({ lvl: 'na', text: /^1\.2\.804\./.test(alg) ? t('c_sig_dstu') : t('c_sig_unsupported', { alg: m.keyAlg || String(m.signatureReason || '').split(':')[0] }) });
    }
    if (m.binding === 'ok') C.push({ lvl: 'ok', text: t('c_binding_ok') });
    else if (m.binding === 'fail') C.push({ lvl: 'bad', text: t('c_binding_fail') });

    if (m.timestamp && m.timestamp.ok === true) C.push({ lvl: 'ok', text: t('c_ts_ok', { time: fmtDate(m.timestamp.time), tsa: m.timestamp.tsa || '—' }) });
    else if (m.timestamp && m.timestamp.ok === false) C.push({ lvl: 'bad', text: t('c_ts_bad') });
    else if (m.signedAt) C.push({ lvl: 'na', text: t('c_time_claimed', { time: fmtDate(m.signedAt) }) });
    else C.push({ lvl: 'na', text: t('c_time_none') });

    if (m.cert) {
        const from = fmtDate(m.certFrom, false), to = fmtDate(m.certTo, false);
        if (m.certAtSigning === 'ok') C.push({ lvl: 'ok', text: t('c_cert_ok', { from: from, to: to }) });
        else if (m.certAtSigning === 'expired') C.push({ lvl: 'warn', text: t('c_cert_expired', { to: to }) });
        else if (m.certAtSigning === 'not_yet') C.push({ lvl: 'warn', text: t('c_cert_not_yet', { from: from }) });
        else C.push({ lvl: 'na', text: t('c_cert_unknown_time', { from: from, to: to }) });
        if (m.certAtSigning === 'ok' && m.certToday === 'expired') C.push({ lvl: 'na', text: t('c_cert_expired_now', { to: to }) });
    }
    if (m.chain.some(function (s) { return s.signature === false; })) C.push({ lvl: 'bad', text: t('c_chain_bad') });

    // Доверие к УЦ.
    if (m.cert) {
        const tr = m.trust, ca = m.issuer ? m.issuer.name : '—';
        if (!tr) C.push({ lvl: 'wait', text: t('c_trust_wait') });
        else if (tr.state === 'ru_accredited') {
            C.push({ lvl: 'ok', text: t('c_trust_ru', { ca: tr.ca.short || tr.ca.name, date: fmtDate(tr.at, false), list: tr.listInfo ? fmtDate(tr.listInfo.date, false) : '—' }) });
            if (tr.nowStatus && tr.nowStatus !== tr.status) C.push({ lvl: 'na', text: t('c_trust_ru_now', { status: tr.nowStatus }) });
        } else if (tr.state === 'ru_not_accredited') C.push({ lvl: 'warn', text: t('c_trust_ru_not', { ca: tr.ca.short || tr.ca.name, date: fmtDate(tr.at, false), status: tr.status || '—', list: tr.listInfo ? fmtDate(tr.listInfo.date, false) : '—' }) });
        else if (tr.state === 'eu_qualified') C.push({ lvl: 'ok', text: t('c_trust_eu', { ca: tr.ca.tsp || tr.ca.name, cc: tr.ca.cc }) });
        else if (tr.state === 'eu_not_qualified') C.push({ lvl: 'warn', text: t('c_trust_eu_not', { ca: tr.ca.tsp || tr.ca.name, cc: tr.ca.cc, status: tr.status || '—' }) });
        else if (tr.state === 'self_signed') C.push({ lvl: 'warn', text: t('c_trust_self') });
        else if (tr.state === 'unavailable' || tr.state === 'offline') C.push({ lvl: 'na', text: t('c_trust_unavailable') });
        else if (tr.state === 'list_mismatch') C.push({ lvl: 'bad', text: t('c_trust_mismatch') });
        else C.push({ lvl: 'warn', text: t('c_trust_none', { ca: ca }) });
    }
    // Отзыв.
    if (m.cert && !(m.trust && m.trust.state === 'self_signed')) {
        const r = m.revocation, mean = m.revMeaning;
        if (!r) C.push({ lvl: 'wait', text: t('c_rev_wait') });
        else if (mean === 'good') C.push({ lvl: 'ok', text: t('c_rev_good', { src: t('rev_src_' + (r.source || 'crl')), date: fmtDate(r.producedAt || r.thisUpdate) }) });
        else if (mean === 'good_expired') C.push({ lvl: 'na', text: t('c_rev_good_expired', { date: fmtDate(r.thisUpdate || r.producedAt, false) }) });
        else if (mean === 'revoked_after') C.push({ lvl: 'ok', text: t('c_rev_after', { date: fmtDate(r.revokedAt), reason: reasonText(r.reason) }) });
        else if (mean === 'revoked_after_claimed') C.push({ lvl: 'warn', text: t('c_rev_after_claimed', { date: fmtDate(r.revokedAt), reason: reasonText(r.reason) }) });
        else if (mean === 'revoked_before') C.push({ lvl: 'bad', text: t('c_rev_before', { date: fmtDate(r.revokedAt), reason: reasonText(r.reason) }) });
        else if (mean === 'unchecked') C.push({ lvl: 'na', text: t('c_rev_unchecked') });
        else C.push({ lvl: 'na', text: t('c_rev_unknown', { why: t('rev_why_' + revWhy(r.error)) }) });
    }
    // Подпись сотрудника сертификатом физлица — полномочия даёт МЧД; дверь в её
    // проверку — когда такой инструмент открыт (S.mchd из блейда).
    if (m.mchdHint) C.push({ lvl: 'na', text: t('c_mchd'), href: S.mchd || null, hrefText: t('mchd_link') });
    if (m.xades && m.xades.role && m.xades.role.length) C.push({ lvl: 'na', text: t('c_xades_role', { role: m.xades.role.join(', ') }) });
    if (m.xades && m.xades.place) C.push({ lvl: 'na', text: t('c_xades_place', { place: m.xades.place }) });
    return C;
}

/* ═══ файлы в рейке ═════════════════════════════════════════════════ */

function roleText(it) {
    if (it.role === 'cms') return t(it.env.detached ? 'role_cms_detached' : 'role_cms_attached');
    if (it.role === 'document' && it.pdfNoSig) return t('role_pdf_nosig');
    return t('role_' + it.role);
}

function renderFiles() {
    const list = els.files;
    list.textContent = '';
    st.items.forEach(function (it) {
        if (it.isArchive) return;
        const li = el('li', 'sc-file');
        li.dataset.role = it.role;
        li.appendChild(el('span', 'sc-file-dot'));
        const tx = el('div', 'sc-file-t');
        const nm = dataEl('span', 'sc-file-n', it.name); nm.title = it.name;
        tx.appendChild(nm);
        let meta = roleText(it) + ' · ' + fmtSize(it.size);
        if (it.from) meta += ' · ' + t(it.from.attached ? 'from_inside' : 'from_archive', { name: it.from.name });
        tx.appendChild(dataEl('span', 'sc-file-m', meta));
        li.appendChild(tx);
        if (!it.from) {
            const x = el('button', 'sc-x', '×');
            x.type = 'button'; x.title = t('remove'); x.setAttribute('aria-label', t('remove') + ' ' + it.name);
            x.addEventListener('click', function () { removeTop(it.top); });
            li.appendChild(x);
        }
        list.appendChild(li);
    });
    const has = st.items.length > 0;
    els.filesWrap.hidden = !has;
    els.root.classList.toggle('sc-has-files', has);
}

function removeTop(top) {
    st.items = st.items.filter(function (i) { return i.top !== top; });
    renderFiles();
    refresh();
}

/* ═══ кап: единица — набор подписей ═════════════════════════════════ */

function setKey() {
    const tops = [];
    st.items.forEach(function (i) { if (SIG_ROLES[i.role] && tops.indexOf(i.top) < 0) tops.push(i.top); });
    if (!tops.length) return null;
    const L = W.__toolLimit;
    const keys = L && L.fileKeys ? L.fileKeys(tops) : tops.map(function (f) { return (f.name || '') + ':' + f.size; });
    return 'set:' + keys.slice().sort().join('|');
}

/* ═══ ход проверки ══════════════════════════════════════════════════ */

// Тот же файл, выбранный ещё раз, — не второй элемент: иначе ответ проверки встаёт в столбец дважды.
function sameFile(a, b) { return !!a && !!b && a.name === b.name && a.size === b.size && a.lastModified === b.lastModified; }

async function addFiles(files, source) {
    if (!files || !files.length) return;
    const list = [];
    Array.prototype.slice.call(files).forEach(function (f) {
        const known = st.items.some(function (i) { return sameFile(i.top, f); }) || list.some(function (g) { return sameFile(g, f); });
        if (!known) list.push(f);
    });
    const dup = files.length - list.length;
    if (dup && W.__toast) W.__toast(t('already_added'), 'info');
    if (!list.length) { ae('verify_files', { n: 0, dup: dup, src: source || 'drop' }); return; }
    busy(t('reading'));
    try {
        for (const f of list) {
            const items = await intakeAll([f]);
            items.forEach(function (it) { it.top = f; it.src = source || 'drop'; st.items.push(it); });
        }
        renderFiles();
        ae('verify_files', { n: list.length, dup: dup, roles: st.items.map(function (i) { return i.role; }).join(','), src: source || 'drop' });
        stage('input', { n: list.length });
    } catch (e) {
        busy('');
        fail(e, 'verify_read', t('err_read'));
        return;
    }
    await refresh();
}

function resetEnv() {
    // Сертификаты, добавленные к подписи в прошлый прогон, — назад.
    st.items.forEach(function (i) {
        if (i.role === 'cms' && i.env) {
            if (i.env._baseCerts == null) i.env._baseCerts = i.env.certificates.length;
            i.env.certificates.length = i.env._baseCerts;
            if (!i.env._baseIdx) i.env._baseIdx = i.env.signers.map(function (s) { return s.certIndex; });
            i.env.signers.forEach(function (s, k) { s.certIndex = i.env._baseIdx[k]; });
        }
    });
}

async function refresh() {
    const run = ++st.run;
    if (!st.items.length) {
        els.results.textContent = '';
        els.results.hidden = true; els.idle.hidden = false;
        els.root.classList.remove('sc-has-result');
        busy('');
        if (st.token) { st.token.abort(); st.token = null; }
        return;
    }
    busy(t('checking'));
    try {
        resetEnv();
        const built = buildCases(st.items);
        await pair(built, function (name, p) { if (run === st.run) busy(t('hashing', { name: name, p: Math.round(p * 100) })); });
        // Кап: проверка набора подписей. Повтор того же набора (и «добавил
        // документ к той же подписи») — та же единица и бесплатно.
        const key = setKey();
        if (key && !st.paidKeys.has(key)) {
            const L = W.__toolLimit;
            const tok = L && L.begin ? L.begin(SLUG, key) : null;
            if (L && L.begin && !tok) { busy(''); renderBlocked(); return; }
            st.token = tok; st.tokenKey = key;
        }
        for (const c of built.cases) {
            if (run !== st.run) return;
            await runCase(c, { docs: built.docs, certPool: built.certPool, progress: function (name, p) { if (run === st.run) busy(t('hashing', { name: name, p: Math.round(p * 100) })); } });
        }
        if (run !== st.run) return;
        await addNoSigCases(built);
        st.cases = built.cases;
        render(built);
        busy('');
        const signed = built.cases.some(function (c) { return c.signers.length || c.type === 'seal'; });
        if (st.token) {
            if (signed) { st.token.commit(); st.paidKeys.add(st.tokenKey); } else st.token.abort();
            st.token = null;
        }
        report(built);
        // Доверие и отзыв — после ответа, по сети.
        enrichAll(built.cases, run);
    } catch (e) {
        if (run !== st.run) return;
        busy('');
        if (st.token) { st.token.abort(); st.token = null; }
        fail(e, 'verify_check', t('err_check'));
    }
}

function renderBlocked() {
    els.results.textContent = '';
    els.results.hidden = true;
    els.idle.hidden = false;
}

/* PDF без подписи: штамп-картинка, пустое поле — или просто документ. */
async function addNoSigCases(built) {
    const paired = new Set(built.cases.map(function (c) { return c.doc && c.doc.id; }).filter(Boolean));
    const lone = (built.unpaired || []).filter(function (d) { return !paired.has(d.id) && !d.isArchive; });
    const anySig = built.cases.length > 0;
    for (const d of lone) {
        if (!d.pdfNoSig && !d.brokenSig && !d.tooBig && anySig) continue;
        const c = { id: 'n' + d.id, type: 'nosig', item: d, title: d.name, signers: [], notes: [], doc: { name: d.name, blob: d.blob, size: d.size, source: 'nosig' } };
        // Больше потолка разбора (PDF 512 МБ, архив 300 МБ) файл не читается вовсе —
        // это не «подписи нет», а «не проверили» (раньше выходило «нет подписи»).
        if (d.tooBig) { c.code = 'too_big'; c.doc = null; }
        else if (d.brokenSig) { c.code = 'broken_sig'; c.doc = null; }
        else if (d.pdfNoSig) {
            const info = await pdfNoSigInfo(d);
            c.code = info.stamp ? 'pdf_stamp' : (info.field ? 'pdf_field' : 'nosig');
            c.pdfInfo = info;
        } else c.code = 'nosig';
        built.cases.push(c);
    }
    built.unpaired = (built.unpaired || []).filter(function (d) { return !d.isArchive && !built.cases.some(function (c) { return c.item === d; }); });
}

async function pdfNoSigInfo(d) {
    const out = { field: false, stamp: false };
    try {
        const b = d.bytes || new Uint8Array(await d.blob.arrayBuffer());
        let s = '';
        for (let i = 0; i < b.length; i += 65536) s += String.fromCharCode.apply(null, b.subarray(i, Math.min(b.length, i + 65536)));
        out.field = /\/FT\s*\/Sig\b/.test(s);
        const pdf = await pdfjs();
        if (pdf) {
            const doc = await pdf.getDocument({ data: b.slice(0), isEvalSupported: false }).promise;
            const pages = [1, 2, doc.numPages - 1, doc.numPages].filter(function (p, i, a) { return p >= 1 && p <= doc.numPages && a.indexOf(p) === i; });
            let text = '';
            for (const p of pages) { const tc = await (await doc.getPage(p)).getTextContent(); text += ' ' + tc.items.map(function (x) { return x.str; }).join(' '); }
            out.stamp = /подписан[а-я]*\s+электронн|электронн[а-я]*\s+подпис|сертификат[\s:]*[0-9a-f]{8,}|digitally signed|signed electronically|elektronisch signiert|firmado (digital|electr)|firmato digitalmente|assinado (digital|eletr)|signé électroniquement|ditandatangani secara elektronik|전자서명|موقع إلكترونيًا|підписан[а-я]*\s+електронн/i.test(text);
            doc.destroy();
        }
    } catch (e) { /* no text layer — not a verdict */ }
    return out;
}

let pdfjsP = null;
function pdfjs() {
    if (!pdfjsP) {
        pdfjsP = import(S.pdfjs.lib).then(function (m) { m.GlobalWorkerOptions.workerSrc = S.pdfjs.worker; return m; }).catch(function () { pdfjsP = null; return null; });
    }
    return pdfjsP;
}

async function enrichAll(cases, run) {
    const pending = cases.filter(function (c) { return c.signers.some(function (m) { return m.cert; }); });
    if (!pending.length) return;
    // Случаи и подписанты — параллельно; пункт «УЦ» встаёт, как только известен.
    await Promise.all(pending.map(async function (c) {
        await enrichCase(c, function (m) { if (run === st.run) { updateSigner(m); updateVerdict(c); } });
        if (run === st.run) updateVerdict(c);
    }));
    if (run !== st.run) return;
    ae('verify_trust', {
        states: [].concat.apply([], pending.map(function (c) { return c.signers.map(function (m) { return m.trust ? m.trust.state : 'none'; }); })).join(','),
        rev: [].concat.apply([], pending.map(function (c) { return c.signers.map(function (m) { return m.revMeaning || 'none'; }); })).join(','),
        codes: pending.map(function (c) { return c.code; }).join(',')
    });
}

function report(built) {
    const codes = built.cases.map(function (c) { return c.code; });
    const gost = built.cases.some(function (c) { return c.signers.some(function (m) { return m.gost; }); });
    ae('verify', { types: built.cases.map(function (c) { return c.type; }).join(','), codes: codes.join(','), gost: gost ? 1 : 0, unpaired: (built.unpaired || []).length });
    stage('verified', { n: built.cases.length });
    try { if (W.__aeToolComplete) W.__aeToolComplete({ action: 'verify', n: built.cases.length, result: codes[0] || 'none' }); } catch (e) { /* noop */ }
    if (els.announce) els.announce.textContent = built.cases.length ? verdictTitle(built.cases[0]) : '';
}

/* ═══ отрисовка ═════════════════════════════════════════════════════ */

function render(built) {
    const out = els.results;
    out.textContent = '';
    const draw = function () {
        built.cases.forEach(function (c) { out.appendChild(renderCase(c)); });
        if ((built.unpaired || []).length) out.appendChild(el('p', 'sc-note', t('unpaired', { names: built.unpaired.map(function (d) { return d.name; }).join(', ') })));
    };
    draw();
    out.hidden = false; els.idle.hidden = true;
    els.root.classList.add('sc-has-result');
    // Ширину сцены решает раскладка (и широкая рабочая область — после показа ответа).
    requestAnimationFrame(function () { requestAnimationFrame(maybeAutoOpen); });
}

function verdictBlock(c) {
    const lvl = lvlOf(c.code, c);
    const box = el('div', 'sc-verdict');
    const ic = el('div', 'sc-vico');
    ic.appendChild(icon(lvl === 'ok' ? 'ok' : lvl === 'bad' ? 'bad' : lvl === 'warn' ? 'warn' : 'info'));
    box.appendChild(ic);
    const tx = el('div', 'sc-vtxt');
    tx.appendChild(el('h3', 'sc-vt', verdictTitle(c)));
    const who = whoLine(c);
    if (who) tx.appendChild(dataEl('p', 'sc-vs', who));
    let sub = '';
    if (c.code === 'ok' && untrusted(c)) sub = t('v_ok_untrusted');
    else if (c.code === 'broken_sig') sub = t('v_broken_sig_sub');
    else if (c.code === 'too_big') sub = t('v_too_big_sub');
    else if (c.code === 'revision') sub = t('v_revision_sub', { size: fmtSize(c.modifiedAfter) });
    else if (c.code === 'pdf_stamp') sub = t('v_pdf_stamp_sub');
    else if (c.code === 'pdf_field' || (c.code === 'nosig' && c.item && c.item.pdfNoSig)) sub = t('v_pdf_nosig_sub');
    else if (c.type === 'cert') sub = t('v_cert_only_sub');
    if (sub) tx.appendChild(el('p', 'sc-vn', sub));
    box.appendChild(tx);
    return box;
}

function updateVerdict(c) {
    c.code = caseCode(c);
    if (!c._ui) return;
    const fresh = verdictBlock(c);
    c._ui.verdict.replaceWith(fresh);
    c._ui.verdict = fresh;
    c._ui.art.className = 'sc-case sc-l-' + lvlOf(c.code, c) + (c._ui.art.querySelector(':scope > .sc-case-doc') ? ' sc-has-doc' : '');
}

function docCaption(c) {
    const src = c.pairedBy ? { name: 'doc_paired_name', hash: 'doc_paired_hash', name_mismatch: 'doc_paired_mismatch', single: 'doc_paired_single' }[c.pairedBy]
        : { inside: 'doc_inside', pdf: 'doc_pdf', container: 'doc_container', eml: 'doc_eml', xml_enveloped: 'doc_xml_enveloped', inner_signed: 'doc_inner_signed', paired: 'doc_paired_hash' }[c.doc.source];
    return fmtSize(c.doc.size || (c.doc.blob && c.doc.blob.size) || 0) + (src ? ' · ' + t(src) : '');
}

async function docName(c) {
    const d = c.doc;
    let name = d.name || 'document';
    if (d.source === 'inside' || d.source === 'inner_signed') {
        const sn = await sniffDoc(d);
        if (!/\.[A-Za-z0-9]{2,5}$/.test(name) || name === c.item.name) name = name.replace(/\.[^.]*$/, '') + '.' + sn.ext;
    }
    return safeName(name, 'document');
}

function renderDoc(c, art) {
    if (!c.doc) {
        if (c.type === 'cms' && c.code === 'need_doc') {
            const box = el('div', 'sc-needdoc');
            box.appendChild(el('p', null, t('v_need_doc_sub')));
            box.appendChild(btn(t('add_doc'), '', pickMore));
            art.appendChild(box);
        }
        return;
    }
    const row = el('div', 'sc-docrow');
    const tx = el('div', 'sc-docrow-t');
    tx.appendChild(dataEl('b', null, c.doc.name));
    tx.appendChild(el('span', null, docCaption(c)));
    row.appendChild(tx);
    const acts = el('div', 'sc-acts');
    const viewer = el('div', 'sc-viewer');
    viewer.hidden = true;
    const openBtn = btn(t('doc_open'), 'ghost', function () {
        if (!viewer.hidden) { viewer.hidden = true; openBtn.textContent = t('doc_open'); return; }
        viewer.hidden = false; openBtn.textContent = t('doc_close');
        if (!viewer.dataset.ready) {
            viewer.dataset.ready = '1';
            showDoc(c, viewer).catch(function (e) {
                viewer.textContent = '';
                viewer.appendChild(el('p', 'sc-note', t('doc_no_preview')));
                fail(e, 'verify_view');
            });
        }
    });
    acts.appendChild(openBtn);
    if (c._ui) c._ui.openDoc = function () { if (viewer.hidden) openBtn.click(); };
    // Подпись к файлу без подписи обычно приходит отдельно — кнопка того же жеста, что у зоны.
    if (c.type === 'nosig') acts.appendChild(btn(t('add_sig'), '', pickMore));
    if (c.doc.source !== 'pdf' && c.doc.source !== 'nosig' && c.doc.source !== 'xml_enveloped') {
        acts.appendChild(btn(t('doc_download'), '', async function () {
            const name = await docName(c);
            const sn = await sniffDoc(c.doc);
            save(new Blob([c.doc.blob], { type: sn.mime }), name);
            ae('doc_download', { ext: sn.ext });
        }));
    }
    row.appendChild(acts);
    art.appendChild(row);
    art.appendChild(viewer);
    if (c.type === 'pdf' && c.modifiedAfter > 0) {
        const last = c.item.sigs[c.item.sigs.length - 1];
        const box = el('div', 'sc-needdoc sc-revision');
        box.appendChild(el('p', null, t('v_revision_sub', { size: fmtSize(c.modifiedAfter) })));
        box.appendChild(btn(t('revision_download'), 'ghost', function () {
            const rev = W.PKI.PdfSig.signedRevision(c.item.bytes, last);
            save(new Blob([rev], { type: 'application/pdf' }), safeName(c.item.name.replace(/\.pdf$/i, '') + ' — ' + t('revision_suffix') + '.pdf'));
            ae('pdf_revision_download');
        }));
        art.appendChild(box);
    }
    if (c.type === 'asic' && c.containerFiles && c.containerFiles.length > 1) {
        const box = el('div', 'sc-docrow');
        const tx2 = el('div', 'sc-docrow-t');
        tx2.appendChild(el('b', null, t('container_files')));
        const ul = el('ul', 'sc-timeline');
        c.containerFiles.forEach(function (f) {
            const li = el('li');
            const a = el('button', 'sc-linkbtn');
            a.type = 'button';
            a.appendChild(el('bdi', null, f.name));
            a.addEventListener('click', function () { save(f.blob, safeName(f.name.split('/').pop())); ae('container_file_download'); });
            li.appendChild(a);
            li.appendChild(el('span', null, fmtSize(f.size) + ' · ' + t(f.covered ? 'container_covered' : 'container_uncovered')));
            ul.appendChild(li);
        });
        tx2.appendChild(ul);
        box.appendChild(tx2);
        art.appendChild(box);
    }
}

async function showDoc(c, host) {
    host.textContent = '';
    const d = c.doc;
    const sn = await sniffDoc(d);
    ae('doc_open', { kind: sn.kind, ext: sn.ext });
    if (sn.kind === 'pdf') return showPdf(d.blob, host);
    if (sn.kind === 'image') {
        const img = el('img');
        img.alt = d.name;
        img.src = URL.createObjectURL(new Blob([d.blob], { type: sn.mime }));
        host.appendChild(img);
        return;
    }
    if (sn.kind === 'xml') return showXml(d.blob, host);
    if (sn.kind === 'text') {
        const b = new Uint8Array(await d.blob.slice(0, 2 * 1024 * 1024).arrayBuffer());
        let txt;
        try { txt = new TextDecoder('utf-8', { fatal: true }).decode(b); } catch (e) { txt = new TextDecoder('windows-1251').decode(b); }
        host.appendChild(el('pre', 'sc-pre', txt));
        return;
    }
    host.appendChild(el('p', 'sc-note', t('doc_no_preview')));
}

async function showPdf(blob, host) {
    const lib = await pdfjs();
    if (!lib) { host.appendChild(el('p', 'sc-note', t('doc_no_preview'))); return; }
    const data = new Uint8Array(await blob.arrayBuffer());
    const doc = await lib.getDocument({ data: data, isEvalSupported: false }).promise;
    const pages = el('div', 'sc-pages');
    host.appendChild(el('p', 'sc-note', t('doc_pages', { n: doc.numPages })));
    host.appendChild(pages);
    let next = 1;
    const more = btn(t('doc_more_pages'), 'ghost', function () { drawMore(); });
    async function drawMore() {
        const width = Math.max(280, Math.min(host.clientWidth - 20, 900));
        const stop = Math.min(doc.numPages, next + 2);
        for (; next <= stop; next++) {
            const page = await doc.getPage(next);
            const vp1 = page.getViewport({ scale: 1 });
            const scale = width / vp1.width;
            const ratio = Math.min(2, W.devicePixelRatio || 1);
            const vp = page.getViewport({ scale: scale * ratio });
            const cv = el('canvas');
            cv.width = Math.round(vp.width); cv.height = Math.round(vp.height);
            cv.style.width = Math.round(vp.width / ratio) + 'px';
            cv.setAttribute('aria-label', String(next));
            pages.appendChild(cv);
            await page.render({ canvasContext: cv.getContext('2d'), viewport: vp }).promise;
        }
        more.hidden = next > doc.numPages;
    }
    host.appendChild(more);
    await drawMore();
}

async function showXml(blob, host) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let doc;
    try { doc = parseXml(bytes); } catch (e) { host.appendChild(el('pre', 'sc-pre', decodeXml(bytes).text.slice(0, 500000))); return; }
    const wrap = el('div', 'sc-xml');
    const seg = el('div', 'sc-seg');
    const readable = el('div');
    const raw = el('pre', 'sc-pre');
    raw.hidden = true;
    const b1 = btn(t('doc_xml_readable'), '', function () { readable.hidden = false; raw.hidden = true; });
    const b2 = btn(t('doc_xml_raw'), 'ghost', function () { if (!raw.dataset.ready) { raw.dataset.ready = '1'; raw.textContent = pretty(doc, 1500000); } raw.hidden = false; readable.hidden = true; });
    const b3 = btn(t('doc_print'), 'ghost', function () { printNode(readable); });
    seg.append(b1, b2, b3);
    wrap.appendChild(seg);
    const sum = summarize(doc, S.locale);
    if (sum && sum.rows.length) {
        const dl = el('dl', 'sc-xml-sum');
        sum.rows.forEach(function (r) { dl.appendChild(el('dt', null, r[0])); dl.appendChild(dataEl('dd', null, r[1])); });
        readable.appendChild(dl);
    }
    readable.appendChild(renderTree(doc, S.locale));
    if (S.einvoice && isInvoice(doc)) {
        const a = el('a', 'tc-btn sm ghost', t('open_einvoice'));
        a.href = S.einvoice;
        seg.appendChild(a);
    }
    wrap.append(readable, raw);
    host.appendChild(wrap);
}

function isInvoice(doc) {
    const r = doc.root;
    if (r.local === 'FatturaElettronica' || r.local === 'Invoice' || r.local === 'CreditNote' || r.local === 'CrossIndustryInvoice') return true;
    // УПД / счёт-фактура ФНС: <Файл ИдФайл="ON_NSCHFDOPPR_…">
    const id = (r.attrs.find(function (a) { return a.local === 'ИдФайл'; }) || {}).value || '';
    return /^ON_(NSCHF|NKORSCHF)/.test(id);
}

// Печать — через общую дверь __printWindow: прежний window.open(…, 'noopener')
// по спецификации ВСЕГДА возвращает null, и кнопка «Печать» не делала ничего.
function printNode(node) {
    const clone = node.cloneNode(true);
    clone.querySelectorAll('details').forEach(function (x) { x.setAttribute('open', ''); });
    const esc = function (s) { return String(s).replace(/[&<>"]/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]; }); };
    const css = 'body{font:14px/1.5 system-ui,sans-serif;margin:24px;color:#111}dt{font-weight:600;color:#444}dl{display:grid;grid-template-columns:auto 1fr;gap:4px 14px}details{margin-inline-start:12px}summary{font-weight:600}.sc-kv{display:flex;gap:10px;padding-inline-start:12px}.sc-kv span:first-child{color:#555;min-width:140px}';
    const html = '<!doctype html><html lang="' + esc(S.locale || 'en') + '" dir="' + (document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr') + '"><head><meta charset="utf-8"><title>' + esc(document.title) + '</title><style>' + css + '</style></head><body>' + clone.outerHTML + '</body></html>';
    const w = W.__printWindow ? W.__printWindow(html, { onBlocked: function () { if (W.__toast) W.__toast(t('print_blocked'), 'warning'); } }) : null;
    ae('doc_print', { ok: w ? 1 : 0 });
}

function initials(name) {
    const w = String(name || '').replace(/[«»"'().,]/g, ' ').split(/\s+/).filter(Boolean);
    return w.length ? (w[0][0] + (w[1] ? w[1][0] : '')).toUpperCase() : '?';
}

function badgesOf(m) {
    const out = [];
    if (m.kind === 'counter') out.push(['', t('b_counter')]);
    if (m.kind === 'doc_ts') out.push(['', t('b_doc_ts')]);
    if (m.qualified === 'ru') out.push(['q', t('b_qualified_ru')]);
    if (m.qualified === 'eu') out.push(['q', t('b_qualified_eu')]);
    if (m.qualified === 'eu_qscd') out.push(['q', t('b_qualified_eu_qscd')]);
    if (m.gost) out.push(['', t('b_gost')]); else if (m.keyAlg) out.push(['', m.keyAlg]);
    if (m.cert && m.cert.selfIssued) out.push(['w', t('b_self')]);
    return out;
}

function fillChecks(ul, m) {
    ul.textContent = '';
    checksOf(m).forEach(function (c) {
        const li = el('li', 'sc-check sc-c-' + c.lvl);
        li.appendChild(icon('c-' + c.lvl));
        li.appendChild(dataEl('span', null, c.text));
        if (c.action === 'add_doc' || c.action === 'add_cert') li.appendChild(btn(t(c.action === 'add_doc' ? 'add_doc' : 'add_sig'), 'ghost', pickMore));
        if (c.href) { const a = el('a', 'sc-check-a', c.hrefText); a.href = c.href; a.addEventListener('click', function () { ae('mchd_open'); }); li.appendChild(a); }
        ul.appendChild(li);
    });
}

function renderSigner(m) {
    const card = el('section', 'sc-signer');
    const head = el('div', 'sc-shead');
    head.appendChild(el('div', 'sc-avatar', initials(m.name || m.cn)));
    const who = el('div', 'sc-who');
    who.appendChild(dataEl('b', null, m.name || (m.kind === 'doc_ts' ? t('tsa') : t('unknown_signer'))));
    const role = [m.title, m.org && m.org !== m.name ? m.org : null].filter(Boolean).join(' · ');
    if (role) who.appendChild(dataEl('span', null, role));
    if (m.ids.length) {
        const ids = el('div', 'sc-ids');
        m.ids.slice(0, 4).forEach(function (i) { const s = el('span', null, t('id_' + i.k) + ' '); s.appendChild(dataEl('b', null, i.v)); ids.appendChild(s); });
        who.appendChild(ids);
    }
    head.appendChild(who);
    const badges = el('div', 'sc-badges');
    badgesOf(m).forEach(function (b) { badges.appendChild(el('span', 'sc-badge ' + b[0], b[1])); });
    head.appendChild(badges);
    card.appendChild(head);
    const ul = el('ul', 'sc-checks');
    fillChecks(ul, m);
    card.appendChild(ul);
    if (m.cert) card.appendChild(certDrawer(m));
    m._ui = { card: card, checks: ul };
    if (m.counter && m.counter.length) {
        const box = el('div', 'sc-counter');
        m.counter.forEach(function (cm) { box.appendChild(renderSigner(cm)); });
        card.appendChild(box);
    }
    return card;
}

function updateSigner(m) {
    if (m._ui) fillChecks(m._ui.checks, m);
    (m.counter || []).forEach(updateSigner);
}

function certDrawer(m) {
    const det = el('details', 'tc-drawer');
    const sum = el('summary');
    sum.appendChild(el('span', null, t('cert_details')));
    if (m.issuer) sum.appendChild(dataEl('span', 'tc-drawer-sub', m.issuer.name));
    det.appendChild(sum);
    const body = el('div', 'tc-drawer-body');
    det.addEventListener('toggle', async function () {
        if (!det.open || body.dataset.ready) return;
        body.dataset.ready = '1';
        const dl = el('dl', 'sc-kvs');
        const kv = function (k, v, mono) { if (!v) return; dl.appendChild(el('dt', null, t(k))); dl.appendChild(dataEl('dd', mono ? 'sc-mono' : null, v)); };
        kv('k_owner', m.name); kv('k_title', m.title); kv('k_org', m.org);
        m.ids.forEach(function (i) { dl.appendChild(el('dt', null, t('id_' + i.k))); dl.appendChild(dataEl('dd', 'sc-mono', i.v)); });
        kv('k_email', m.email);
        kv('k_place', m.locality);
        kv('k_issuer', m.issuer ? [m.issuer.name, m.issuer.inn ? t('id_innle') + ' ' + m.issuer.inn : '', m.issuer.ogrn ? t('id_ogrn') + ' ' + m.issuer.ogrn : ''].filter(Boolean).join(', ') : '');
        kv('k_valid', fmtDate(m.certFrom, false) + ' — ' + fmtDate(m.certTo, false));
        kv('k_serial', m.serial, true); kv('k_key', m.keyAlg); kv('k_hash', m.hashAlg);
        kv('k_format', [m.format, m.level].filter(Boolean).join(' '));
        const pr = await certPrints(m);
        if (pr) { kv('k_sha1', pr.sha1, true); kv('k_sha256', pr.sha256, true); }
        if (m.cert.crlUrls && m.cert.crlUrls.length) kv('k_crl', m.cert.crlUrls.join(' '), true);
        if (m.cert.ocspUrls && m.cert.ocspUrls.length) kv('k_ocsp', m.cert.ocspUrls.join(' '), true);
        body.appendChild(dl);
        const acts = el('div', 'sc-acts');
        acts.appendChild(btn(t('cert_download'), 'ghost', function () {
            save(new Blob([m.certDer], { type: 'application/pkix-cert' }), safeName(m.name || 'certificate', 'certificate') + '.cer');
            ae('cert_download');
        }));
        body.appendChild(acts);
    });
    det.appendChild(body);
    return det;
}

function timeline(c) {
    const ev = [];
    c.signers.forEach(function (m) {
        if (m.when) ev.push({ when: m.when, who: m.name || (m.kind === 'doc_ts' ? t('tsa') : '—'), what: m.kind === 'doc_ts' ? 'tl_stamped' : 'tl_signed' });
        (m.counter || []).forEach(function (x) { if (x.when) ev.push({ when: x.when, who: x.name || '—', what: 'tl_countersigned' }); });
    });
    if (ev.length < 2) return null;
    ev.sort(function (a, b) { return a.when - b.when; });
    const box = el('div');
    box.appendChild(el('p', 'tc-eyebrow', t('timeline')));
    const ol = el('ol', 'sc-timeline');
    ev.forEach(function (e) {
        const li = el('li');
        const tm = el('time', null, fmtDate(e.when));
        tm.dateTime = new Date(e.when).toISOString();
        li.appendChild(tm);
        li.appendChild(dataEl('span', null, e.who + ' — ' + t(e.what)));
        ol.appendChild(li);
    });
    box.appendChild(ol);
    return box;
}

// Карточка случая — две колонки: ответ (вердикт, подписанты, пункты, протокол) и
// документ. На узкой сцене колонки растворены (display:contents) и порядок прежний:
// вердикт → документ → подписанты; на широкой (сцена ≥ 1000 px) документ стоит
// рядом с ответом и открывается сам.
function renderCase(c) {
    const art = el('article', 'sc-case sc-l-' + lvlOf(c.code, c));
    const main = el('div', 'sc-case-main');
    const side = el('div', 'sc-case-doc');
    const v = verdictBlock(c);
    main.appendChild(v);
    c._ui = { art: art, verdict: v };
    renderDoc(c, side);
    const tl = timeline(c);
    if (tl) main.appendChild(tl);
    if (c.signers.length) {
        const list = el('div', 'sc-signers');
        c.signers.forEach(function (m) { m.inside = insideDoc(c); list.appendChild(renderSigner(m)); });
        main.appendChild(list);
        const acts = el('div', 'sc-acts');
        acts.appendChild(btn(t('report_pdf'), 'ghost', function (e) { makeReport(c, e.currentTarget); }));
        // Итог текстом — переслать коллеге в мессенджер без файла протокола.
        acts.appendChild(btn(t('copy_summary'), 'ghost', function () { copySummary(c); }));
        main.appendChild(acts);
        // Подписанный PDF на бумаге — с отметкой об ЭП (ГОСТ Р 7.0.97): кнопка
        // встаёт, когда понятно, что документ — PDF, а подпись верна.
        if (c.doc && c.doc.blob && c.code === 'ok') {
            sniffDoc(c.doc).then(function (sn) {
                if (!stampable(c, sn.kind)) return;
                const b = btn(t('stamp_btn'), 'ghost', function (e) { makeStamp(c, e.currentTarget); });
                b.title = t('stamp_hint');
                acts.appendChild(b);
            }, function () { /* no kind — no button */ });
        }
    }
    art.appendChild(main);
    if (side.childNodes.length) { art.appendChild(side); art.classList.add('sc-has-doc'); }
    return art;
}

const MARK = { ok: '✓', warn: '!', bad: '✗', na: '–', wait: '…' };
function summaryText(c) {
    const out = [verdictTitle(c)];
    const who = whoLine(c);
    if (who) out.push(who);
    if (c.doc && c.doc.name) out.push(c.doc.name);
    c.signers.forEach(function (m) {
        out.push('');
        out.push(m.name || m.cn || t('unknown_signer'));
        checksOf(m).forEach(function (x) { out.push(MARK[x.lvl] + ' ' + x.text); });
    });
    out.push('');
    out.push(fmtDate(new Date()) + ' · ' + location.origin + location.pathname);
    return out.join('\n');
}
async function copySummary(c) {
    const txt = summaryText(c);
    let ok = false;
    try { await navigator.clipboard.writeText(txt); ok = true; } catch (e) {
        try {
            const ta = document.createElement('textarea');
            ta.value = txt; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
            document.body.appendChild(ta); ta.select(); ok = document.execCommand('copy'); ta.remove();
        } catch (e2) { ok = false; }
    }
    if (W.__toast) W.__toast(ok ? t('copied') : t('copy_failed'), ok ? 'success' : 'warning');
    ae('summary_copy', { code: c.code, ok: ok ? 1 : 0 });
}

/* Широкая сцена: документ первого случая открывается сам — место для него есть. */
const WIDE_STAGE = 1000;
function maybeAutoOpen() {
    if (els.results.hidden || st.autoOpened === st.run) return;
    if (els.results.clientWidth < WIDE_STAGE) return;
    const c = (st.cases || []).find(function (x) { return x._ui && x._ui.openDoc; });
    if (!c) return;
    st.autoOpened = st.run;
    c._ui.openDoc();
    ae('doc_auto_open', { w: Math.round(els.results.clientWidth) });
}

async function makeReport(c, button) {
    const old = button.textContent;
    button.disabled = true; button.textContent = t('report_wait');
    try {
        const blob = await protocolPdf(c, {
            t: t, fmtDate: fmtDate, fmtSize: fmtSize, verdictTitle: verdictTitle, checksOf: checksOf, lvlOf: lvlOf,
            site: S.site || location.host, lang: S.locale, paper: S.paper, listsLine: listsLine(c)
        });
        await save(blob, safeName(t('r_title') + ' — ' + stripSigExt(c.title || 'document').replace(/\.[A-Za-z0-9]{1,5}$/, '')) + '.pdf');
        ae('report_pdf', { code: c.code });
    } catch (e) {
        fail(e, 'verify_report', t('err_report'));
    } finally {
        button.disabled = false; button.textContent = old;
    }
}

async function makeStamp(c, button) {
    if (c.code !== 'ok') { if (W.__toast) W.__toast(t('stamp_not_valid'), 'warning'); return; }
    const old = button.textContent;
    button.disabled = true; button.textContent = t('stamp_wait');
    try {
        const res = await stampPdf(c, { t: t, fmtDate: fmtDate, site: S.site || location.host, lang: S.locale, pdfjs: pdfjs });
        const base = stripSigExt(c.doc.name || 'document').replace(/\.pdf$/i, '');
        const saved = await save(res.blob, safeName(base + ' — ' + t('stamp_suffix')) + '.pdf');
        if (saved !== false && W.__toast) W.__toast(t(res.placed === 'sheet' ? 'stamp_done_sheet' : 'stamp_done'), 'success');
        ae('stamp_pdf', { code: c.code, n: res.n, placed: res.placed });
    } catch (e) {
        fail(e, 'verify_stamp', t('err_stamp'));
    } finally {
        button.disabled = false; button.textContent = old;
    }
}

function listsLine(c) {
    let ru = null, eu = null;
    c.signers.forEach(function (m) {
        if (m.trust && m.trust.listInfo) {
            if (m.trust.list === 'ru') ru = (m.trust.listInfo.version || '') + ', ' + fmtDate(m.trust.listInfo.date, false);
            if (m.trust.list === 'eu') eu = fmtDate(m.trust.listInfo.fetched_at, false);
        }
    });
    // Называем только списки, которые этой проверке действительно ответили.
    if (ru && eu) return t('r_lists', { ru: ru, eu: eu });
    if (ru) return t('r_lists_ru', { ru: ru });
    if (eu) return t('r_lists_eu', { eu: eu });
    return '';
}

/* ═══ ввод ══════════════════════════════════════════════════════════ */

function pickMore() {
    const inp = document.querySelector('[data-uploader-for="scIn"]');
    if (inp) inp.click();
}

function clearAll() {
    st.items = [];
    st.run++;
    renderFiles();
    refresh();
    ae('verify_clear');
}

async function loadExample(key) {
    const ex = S.examples && S.examples[key];
    if (!ex) return;
    busy(t('reading'));
    try {
        const r = await fetch(ex.url);
        if (!r.ok) throw new Error('example_' + r.status);
        const b = await r.arrayBuffer();
        st.items = [];
        ae('verify_example', { key: key });
        await addFiles([new File([b], ex.name)], 'example');
    } catch (e) {
        busy('');
        fail(e, 'verify_example', t('err_example'));
    }
}

function init() {
    els = {
        root: document.querySelector('.sc'), zone: $('scIn'), files: $('scFiles'), filesWrap: $('scFilesWrap'), clear: $('scClear'),
        paste: $('scPaste'), pasteBtn: $('scPasteBtn'), pasteDrawer: $('scPasteDrawer'),
        busy: $('scBusy'), busyText: $('scBusyText'), idle: $('scIdle'), results: $('scResults'), announce: $('scAnnounce')
    };
    if (!els.root) return;
    if (W.__bindUploaderFile && els.zone) {
        W.__bindUploaderFile(els.zone, function (f, detail) {
            const files = detail && detail.files ? Array.prototype.slice.call(detail.files) : [f];
            addFiles(files, detail && detail.source);
            if (els.zone.__uploaderReset) els.zone.__uploaderReset();
        });
    }
    els.clear.addEventListener('click', clearAll);
    els.pasteBtn.addEventListener('click', function () {
        const txt = (els.paste.value || '').trim();
        if (!txt) return;
        els.paste.value = '';
        els.pasteDrawer.open = false;
        ae('verify_paste', { len: txt.length });
        addFiles([new File([new TextEncoder().encode(txt)], t('pasted_name') + '.p7s', { type: 'text/plain' })], 'paste');
    });
    els.root.querySelectorAll('[data-example]').forEach(function (b) { b.addEventListener('click', function () { loadExample(b.dataset.example); }); });
    // Бросок на любое место прибора ловит document-signer/app.js (он знает,
    // какая вкладка открыта) и отдаёт сюда через __SC_APP.addFiles.
    const ww = els.root.closest('[data-wide-workspace]') || els.root;
    ww.addEventListener('wide-workspace:change', function () { requestAnimationFrame(maybeAutoOpen); });
    W.__SC_APP = { addFiles: addFiles, state: st, refresh: refresh, reset: function () { st.items = []; st.run++; renderFiles(); } };
    try { W.dispatchEvent(new Event('sc-app-ready')); } catch (e) { /* old engines */ }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
